/**
 * RadioPopover
 * Reusable single-select filter popover with olive radio circles.
 * Immediate apply -- selecting an option closes the popover.
 * Used for: Sort By filters.
 */

import { useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FilterChipButton } from "./FilterChipButton"

interface RadioOption {
  readonly value: string
  readonly label: string
}

interface RadioPopoverProps {
  /** Label shown on the trigger chip (e.g. "Sort by") */
  label: string
  /** Options to display as radio items */
  options: readonly RadioOption[]
  /** Currently selected value */
  value: string
  /** Called when user selects an option */
  onChange: (value: string) => void
}

export function RadioPopover({ label, options, value, onChange }: RadioPopoverProps) {
  const [open, setOpen] = useState(false)
  const selectedLabel = options.find(o => o.value === value)?.label ?? value

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FilterChipButton label={`${label}:`} value={selectedLabel} isActive={open} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-auto bg-popover border border-border rounded-[20px] p-[20px]"
      >
        <div className="flex flex-col gap-[15px]">
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value)
                setOpen(false)
              }}
              className="flex items-center gap-[10px]"
            >
              <span className="size-[24px] rounded-full border-2 border-brand-olive flex items-center justify-center shrink-0">
                {value === option.value && (
                  <span className="size-[12px] rounded-full bg-brand-olive" />
                )}
              </span>
              <span
                className={`text-[14px] leading-[25px] text-text-primary ${value === option.value ? "font-semibold" : "font-normal"}`}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
